import { EquipmentQuantity } from '@/util/types/serverTypes';

/**
 * Merges calculated equipment adjustments with current equipment quantities
 * 
 * This function respects manual user overrides via the `manualChange` flag.
 * If a user manually sets equipment quantities, those values are preserved
 * and not overwritten by automatic calculations.
 * 
 * @param adjustments - Equipment quantities calculated from service modifiers
 * @param currentEquipment - Current equipment quantities (may include manual changes)
 * @returns Object with merged equipment and whether changes occurred
 */
export function mergeEquipmentWithOverrides(
  adjustments: EquipmentQuantity[],
  currentEquipment: EquipmentQuantity[]
): {
  merged: EquipmentQuantity[];
  hasChanges: boolean;
} {
  const quantityMap = new Map<string, EquipmentQuantity>();
  
  // Track which equipment IDs are required by services
  const requiredEquipmentIds = new Set(
    adjustments.map((adj) => adj._equipmentCategoryId)
  );

  // Start with existing equipment quantities, preserving all properties
  currentEquipment.forEach((eq) => {
    quantityMap.set(eq._equipmentCategoryId, { ...eq });
  });

  // Apply adjustments, respecting manual changes
  adjustments.forEach((adjustment) => {
    const existing = quantityMap.get(adjustment._equipmentCategoryId);

    if (existing) {
      // Only apply adjustment if this item wasn't manually changed by user
      if (!existing.manualChange) {
        const newValue = Math.max(0, adjustment.value);
        if (existing.value !== newValue) {
          existing.value = newValue;
        }
      }
    } else {
      // Add new equipment if adjustment is positive
      if (adjustment.value > 0) {
        quantityMap.set(adjustment._equipmentCategoryId, {
          ...adjustment,
          manualChange: false,
        });
      }
    }
  });

  // Only keep equipment that is:
  // 1. Required by current services (in adjustments) AND has positive value, OR
  // 2. Manually changed by user (preserve overrides, even if value is 0)
  const merged = Array.from(quantityMap.values()).filter(
    (eq) => 
      eq.manualChange || 
      (requiredEquipmentIds.has(eq._equipmentCategoryId) && eq.value > 0)
  );

  // Check for meaningful changes to equipment state
  const hasChanges =
    merged.length !== currentEquipment.length ||
    merged.some((newEq) => {
      const existing = currentEquipment.find(
        (eq) => eq._equipmentCategoryId === newEq._equipmentCategoryId
      );
      return (
        !existing ||
        existing.value !== newEq.value ||
        existing.manualChange !== newEq.manualChange
      );
    });

  return { merged, hasChanges };
}

